import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
  Pagination,
  IconButton,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import API from "../api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formatDate } from "../utils/formatDate";
// Custom styled Pagination component
const CustomPagination = styled(Pagination)(({ theme }) => ({
  "&.MuiPagination-root": {
    margin: theme.spacing(1),
    backgroundColor: "#fff",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  "& .MuiPaginationItem-root": {
    minWidth: 36,
    height: 36,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontWeight: 500,
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  "& .MuiPaginationItem-ellipsis": {
    color: theme.palette.text.secondary,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    opacity: 0.6,
  },
}));

// Component for collapsible description text
const CollapsibleText = ({ text, limit = 100 }) => {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= limit) {
    return <span>{text}</span>;
  }
  return (
    <span>
      {expanded ? text : `${text.slice(0, limit)}... `}
      <IconButton size="small" onClick={() => setExpanded(!expanded)}>
        {expanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </IconButton>
    </span>
  );
};

// Helper function to format numbers with 2 decimal places
function formatNumber(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function CategoryReportPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [sortField, setSortField] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportData, setReportData] = useState({
    rows: [],
    totals: {},
    pagination: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  // Set default date range to current month
  const defaultDateRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: first.toISOString().split("T")[0],
      end: last.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const { start, end } = defaultDateRange();
    setStartDate(start);
    setEndDate(end);
    fetchAccounts();
    fetchCategories();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await API.get("/accounts?limit=1000000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Error fetching accounts", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get("/categories?limit=1000000");
      setCategories(res.data || []);
    } catch (err) {
      console.error("Error fetching categories", err);
    }
  };

  const generateReport = async (currentPage = 1) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        startDate,
        endDate,
        account: accountFilter,
        category: categoryFilter,
        page: currentPage,
        limit: 50,
        sortField,
        sortOrder,
      };
      const res = await API.get("/advance-reports/category-report", { params });
      setReportData(res.data);
      setPage(res.data.pagination?.currentPage || currentPage);
    } catch (err) {
      console.error("Error generating report", err);
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = {
        startDate,
        endDate,
        account: accountFilter,
        category: categoryFilter,
        sortField,
        sortOrder,
      };
      const response = await API.get("/advance-reports/category-report/export", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "category-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting CSV", err);
    }
  };

  const handlePageChange = (event, value) => {
    generateReport(value);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        Category Report
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={[{ _id: "ALL", accountName: "All Accounts" }, ...accounts]}
            getOptionLabel={(option) => option.accountName || ""}
            value={
              accountFilter === "ALL"
                ? { _id: "ALL", accountName: "All Accounts" }
                : accounts.find((acc) => acc._id === accountFilter) || null
            }
            onChange={(event, newValue) =>
              setAccountFilter(newValue ? newValue._id : "ALL")
            }
            renderInput={(params) => (
              <TextField {...params} label="Account" variant="outlined" />
            )}
            sx={{ width: 250 }}
          />
          <Autocomplete
            options={[{ _id: "ALL", name: "All Categories" }, ...categories]}
            getOptionLabel={(option) => option.name || ""}
            value={
              categoryFilter === "ALL"
                ? { _id: "ALL", name: "All Categories" }
                : categories.find((cat) => cat._id === categoryFilter) || null
            }
            onChange={(event, newValue) =>
              setCategoryFilter(newValue ? newValue._id : "ALL")
            }
            renderInput={(params) => (
              <TextField {...params} label="Category" variant="outlined" />
            )}
            sx={{ width: 250 }}
          />
          <TextField
            select
            label="Sort By"
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="date">Date</MenuItem>
            <MenuItem value="amount">Amount</MenuItem>
          </TextField>
          <TextField
            select
            label="Order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </TextField>
          <Button variant="contained" onClick={() => generateReport(1)}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={handleExportCSV}>
            Export CSV
          </Button>
        </Stack>
      </Paper>
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.rows &&
                  reportData.rows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.account}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.category}</TableCell>
                      <TableCell>
                        <CollapsibleText text={row.description} limit={100} />
                      </TableCell>
                      <TableCell>{formatNumber(row.debit)}</TableCell>
                      <TableCell>{formatNumber(row.credit)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>
                    Total:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Debit: {formatNumber(reportData.totals?.totalDebit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Credit: {formatNumber(reportData.totals?.totalCredit)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Remaining: {formatNumber(reportData.totals?.remainingTotal)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>
          <Box
            sx={{
              p: 2,
              mt: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#fff",
              borderTop: "1px solid #ddd",
            }}
          >
            <CustomPagination
              page={page}
              count={reportData.pagination?.totalPages || 1}
              onChange={handlePageChange}
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default CategoryReportPage;
